import React, { useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";
import { Sparkles, ArrowRight, Check, X, Plus, ArrowRightLeft, Loader2 } from "lucide-react";

/** Vergleich alt/neu nach «Neu prüfen» – Nutzer übernimmt, legt Konto an, überträgt an andere Firma oder behält. */
export function InboxRecheckCard({ companyId, item, onChanged, onMove }) {
  const rc = item.recheck;
  const [busy, setBusy] = useState(null);
  if (!rc) return null;

  const apply = async (action) => {
    setBusy(action);
    try {
      const r = await apiClient.post(`/companies/${companyId}/inbox/${item.id}/recheck/apply`, { action });
      toast.success(action === "keep" ? "Bisheriges Konto behalten" : `Konto übernommen: ${r.data.suggested_account}`);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Übernehmen fehlgeschlagen");
    } finally { setBusy(null); }
  };

  const btn = "inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium disabled:opacity-50";
  const target = rc.suggested_account || (rc.new_account ? `${rc.new_account.code} ${rc.new_account.name} (neu)` : null);

  return (
    <div className="mt-2 rounded-md border border-violet-200 bg-violet-50/70 px-3 py-2 text-[11px] text-slate-700" data-testid={`inbox-recheck-${item.id}`} onClick={(e) => e.stopPropagation()}>
      <p className="font-medium text-violet-900 flex items-center gap-1.5">
        <Sparkles className="h-3.5 w-3.5" /> Neuprüfung anhand deiner Notiz
      </p>
      <div className="mt-1.5 flex items-center gap-2 flex-wrap" data-testid={`inbox-recheck-compare-${item.id}`}>
        <span className="rounded bg-white border border-slate-200 px-2 py-0.5 line-through decoration-slate-400 text-slate-500">{rc.current_account || "kein Konto"}</span>
        <ArrowRight className="h-3.5 w-3.5 text-violet-500" />
        {target ? (
          <span className={`rounded px-2 py-0.5 font-semibold ${rc.changed || rc.new_account ? "bg-violet-600 text-white" : "bg-emerald-100 text-emerald-800"}`}>
            {target}{!rc.changed && !rc.new_account ? " · bestätigt" : ""}
          </span>
        ) : (
          <span className="rounded bg-amber-100 text-amber-800 px-2 py-0.5">kein passendes Konto gefunden</span>
        )}
      </div>
      {rc.explanation && <p className="mt-1.5 text-slate-700" data-testid={`inbox-recheck-explanation-${item.id}`}>{rc.explanation}</p>}
      {rc.booking_hint && <p className="mt-1 text-slate-600"><span className="font-medium">Buchung:</span> {rc.booking_hint}</p>}
      {rc.move_to_company && (
        <p className="mt-1 text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid={`inbox-recheck-move-hint-${item.id}`}>
          Laut Notiz wurde für <strong>{rc.move_to_company.company_name}</strong> bezahlt – der Beleg kann dorthin übertragen werden.
        </p>
      )}
      <div className="mt-2 flex items-center gap-1.5 flex-wrap">
        {rc.suggested_account && (
          <button type="button" disabled={!!busy} onClick={() => apply("account")} className={`${btn} bg-violet-600 text-white hover:bg-violet-700`} data-testid={`inbox-recheck-apply-${item.id}`}>
            {busy === "account" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} {rc.changed ? "Neues Konto übernehmen" : "Konto bestätigen"}
          </button>
        )}
        {rc.new_account && (
          <button type="button" disabled={!!busy} onClick={() => apply("create_account")} className={`${btn} bg-violet-600 text-white hover:bg-violet-700`} data-testid={`inbox-recheck-create-${item.id}`}>
            {busy === "create_account" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} Konto {rc.new_account.code} anlegen & übernehmen
          </button>
        )}
        {rc.move_to_company && (
          <button type="button" disabled={!!busy} onClick={() => onMove?.(item, rc.move_to_company.company_id)} className={`${btn} border border-amber-300 bg-white text-amber-800 hover:bg-amber-50`} data-testid={`inbox-recheck-move-${item.id}`}>
            <ArrowRightLeft className="h-3 w-3" /> An {rc.move_to_company.company_name} übertragen
          </button>
        )}
        <button type="button" disabled={!!busy} onClick={() => apply("keep")} className={`${btn} border border-slate-300 bg-white text-slate-700 hover:bg-slate-50`} data-testid={`inbox-recheck-keep-${item.id}`}>
          <X className="h-3 w-3" /> Bisheriges behalten
        </button>
      </div>
    </div>
  );
}
